import React, { Fragment, useEffect, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { FaSync } from "react-icons/fa";
import toast from "react-hot-toast";
import { Shimmer } from "react-shimmer";

import { ledgerHeader } from "../../constants/CONSTANT";
import JajotApi from "../../api/JajotApi";
import RecordDataTable from "../RecordDataTable";
import RecordModal from "../common/RecordModal";
import { TopNavigation } from "../layout/TopNavigation";
import { useTallySync } from "../../hooks/useTallySync";
import SyncModal from "../common/SyncModal";
import TaskModal from "../common/TaskModal";

function Ledger() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const { isSyncing, syncStatus, startSync, stopSync } = useTallySync(() => {
    fetchLedgers();
  });

  useEffect(() => {
    fetchLedgers();
  }, []);

  async function fetchLedgers() {
    try {
      setIsLoading(true);
      const result = await JajotApi.getLedgerRecords();
      if (result?.success) {
        setRecords(result.records || []);
      } else {
        setRecords([]);
        toast.error("No ledger records found");
      }
    } catch (err) {
      console.error("Error fetching ledgers:", err);
      toast.error("Failed to fetch ledger records");
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Fragment>
      {/* Top Navigation */}
      <TopNavigation active="Ledgers" />

      {/* Main Content */}
      <Container fluid className="py-3">
        <div className="bg-white shadow-sm rounded p-4">
          {/* Header */}
          <Row className="mb-4 align-items-center">
            <Col>
              <h4 className="fw-bold text-primary mb-0">Ledger Records</h4>
              <small className="text-muted">
                Manage and view all ledger entries
              </small>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                className="shadow-sm py-2 me-2"
                disabled={isLoading || isSyncing}
                onClick={() => startSync("fetch-ledger")}
              >
                <FaSync className="me-2" /> Sync with Tally
              </Button>
              <Button
                className="fw-bold shadow-sm px-4 py-2"
                onClick={() => setShowTaskModal(true)}
                disabled={isLoading}
              >
                Add Task
              </Button>
            </Col>
          </Row>

          {/* Table / Loader / Empty */}
          {isLoading ? (
            <div className="p-3">
              <Shimmer width={600} height={30} className="mb-3" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} />
            </div>
          ) : records.length > 0 ? (
            <RecordDataTable
              tableHeaders={ledgerHeader}
              tableData={records}
              handleRowClick={setSelectedRecord}
              permission={{ update: false, delete: false }}
            />
          ) : (
            <div className="text-center text-muted py-5">
              <h6>No ledger records found</h6>
            </div>
          )}
        </div>
      </Container>

      {/* Modal */}
      {selectedRecord && (
        <RecordModal
          show={!!selectedRecord}
          onHide={() => setSelectedRecord(null)}
          record={selectedRecord}
          header={ledgerHeader}
          defaultType="ledgers"
        />
      )}
      <SyncModal
        show={isSyncing}
        title="Syncing Ledger Records..."
        status={syncStatus}
        onHide={stopSync}
      />
      {showTaskModal && (
        <TaskModal
          show={showTaskModal}
          onHide={() => setShowTaskModal(false)}
          records={records}
          header="Ledger"
        />
      )}
    </Fragment>
  );
}

export default Ledger;
